// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useRouter } from 'expo-router';
import { useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { useMlModels, type MlEvaluation, type MlModel } from './useMlModels';

/**
 * The model registry, for the ML developer page: every trained detector (and the classical
 * thresholds it is measured against), everything train.py recorded about it, and a star rating
 * per feature kind. Read-only; `training/*.py --register` writes it.
 *
 * One card per model rather than a table, because a table of nine models by five kinds showed
 * forty-five dashes: the columns are evaluate.py's per-feature precision/recall, and no model
 * had ever been through evaluate.py. What every model *did* have — its training mIoU, epochs,
 * channels, crop count — the table had no room for. The card leads with that, and says "not
 * evaluated" once, in words, instead of five times in dashes.
 *
 * A card opens: everything the registry holds about that row, down to the course ids and the
 * raw numbers behind each star. "Not evaluated" says which of the reasons it is — no holdout
 * course recorded, a run still waiting for imagery, a run that failed — because the sentence
 * on its own left the next question ("why not?") to a terminal and the jobs table.
 */

const KINDS = ['bunker', 'water', 'green', 'fairway', 'trees', 'path'];

/**
 * An ML job as this panel reads one: enough of `useMlTraining`'s `MlJob` to find the
 * `evaluate` run that belongs to a model and say how it went.
 */
export type MlModelJob = {
  /** extract | train | evaluate | tune */
  kind: string;
  args: string[];
  status: string;
  requestedAt: string;
  error: string | null;
};

export function MlModelsPanel({
  apiBaseUrl,
  token,
  initiallyOpen = false,
  jobs = null,
}: {
  apiBaseUrl: string;
  token: string | null | undefined;
  /** Open without a click. True on the ML developer page, where this is one of the things the page is for. */
  initiallyOpen?: boolean;
  /**
   * The tool runs the page already has, so an unscored model can say which evaluate job it is
   * waiting on. Null — the default, and what the page passes while its own list is loading or
   * failed — means the runs are unknown, which is not the same as there being none.
   */
  jobs?: readonly MlModelJob[] | null;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(initiallyOpen);
  const { models, datasets, loading, error } = useMlModels(apiBaseUrl, token, open);

  if (!open) {
    return (
      <Pressable
        accessibilityRole="button"
        testID="ml-models-open"
        style={styles.openButton}
        onPress={() => setOpen(true)}
      >
        <Text style={styles.openButtonText}>{t('mlModels.open')}</Text>
      </Pressable>
    );
  }

  return (
    <View style={styles.card} testID="ml-models-panel">
      <View style={styles.headerRow}>
        <Text style={styles.title}>{t('mlModels.title')}</Text>
        <Pressable accessibilityRole="button" onPress={() => setOpen(false)}>
          <Text style={styles.close}>{t('mlModels.close')}</Text>
        </Pressable>
      </View>
      <Text style={styles.legend}>{t('mlModels.legend')}</Text>

      {loading ? (
        <ActivityIndicator color={appColors.accent.primary} />
      ) : error === 'unavailable' ? (
        <Text style={styles.notice}>{t('mlModels.unavailable')}</Text>
      ) : error ? (
        <Text style={styles.notice}>{t('mlModels.failed')}</Text>
      ) : models.length === 0 ? (
        <Text style={styles.notice} testID="ml-models-empty">
          {t('mlModels.empty')}
        </Text>
      ) : (
        <>
          {models.map((model) => (
            <ModelCard key={model.id ?? 'classical'} model={model} jobs={jobs} />
          ))}
          {datasets.length > 0 ? (
            <Text style={styles.legend}>{t('mlModels.datasets', { count: datasets.length })}</Text>
          ) : null}
        </>
      )}
    </View>
  );
}

function ModelCard({ model, jobs }: { model: MlModel; jobs: readonly MlModelJob[] | null }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const byKind = new Map(model.latestEvaluations.map((e) => [e.kind, e]));
  const params = describeHyperparams(model.hyperparamsJson);
  const miou = trainingMiou(model.bestValLoss);
  const classical = model.id === null;

  // What train.py recorded, in the order a developer asks about it: how good, how long, on
  // what. Each fact is present only when the row has it, so an old row shows less, not "—".
  const facts = classical
    ? []
    : [
        miou !== null ? t('mlModels.trainingMiou', { value: miou.toFixed(3) }) : null,
        model.epochs > 0 ? t('mlModels.epochs', { count: model.epochs }) : null,
        params.inChannels === 4 ? t('mlModels.channelsNir') : params.inChannels === 3 ? t('mlModels.channelsRgb') : null,
        params.trainCrops !== null && params.trainCourses !== null
          ? t('mlModels.trainedOn', { crops: params.trainCrops, courses: params.trainCourses })
          : null,
        // The size by name: "quick" says which courses and that two quick models compare.
        params.trainSet ? t('mlModels.trainSet', { name: params.trainSet }) : null,
        params.batchSize !== null && params.learningRate !== null
          ? t('mlModels.batchAndRate', { batch: params.batchSize, rate: params.learningRate })
          : null,
        params.providers.length > 0 ? t('mlModels.providers', { providers: params.providers.join(', ') }) : null,
        model.checkpointS3Key ? t('mlModels.checkpointPublished') : t('mlModels.checkpointLocal'),
      ].filter((fact): fact is string => fact !== null);

  return (
    <View style={styles.model} testID={`ml-model-${model.name}-${model.version}`}>
      {/* The whole heading is the target, not a small "details" link: on a card this size the
          name is what a finger and a mouse both aim at. */}
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        testID={`ml-model-toggle-${model.name}-${model.version}`}
        onPress={() => setOpen((wasOpen) => !wasOpen)}
        style={styles.modelHeader}
      >
        <View style={styles.modelHeading}>
          {/* The nickname leads: it is what a person says out loud, and two versions differ by
              four characters in the middle. The exact identity stays right underneath it. */}
          <Text style={styles.modelName}>{model.nickname ?? model.name}</Text>
          <Text style={styles.modelMeta}>
            {[
              model.nickname ? `${model.name} · ${model.version}` : model.version,
              // "unet-resnet34-nir" vs "unet-resnet34": without this, a 4-channel NIR model is
              // indistinguishable from the RGB one it sits beside.
              model.architecture || null,
              model.trainedAt ? formatWhen(model.trainedAt) : null,
            ]
              .filter(Boolean)
              .join('  ·  ')}
          </Text>
        </View>
        <Text style={styles.disclosure}>{open ? t('mlModels.detailsHide') : t('mlModels.detailsShow')}</Text>
      </Pressable>
      {facts.length > 0 ? (
        <Text style={styles.facts} testID={`ml-model-facts-${model.name}-${model.version}`}>
          {facts.join('  ·  ')}
        </Text>
      ) : null}
      {model.datasetDescription ? (
        <Text style={styles.modelMeta}>{t('mlModels.dataset', { description: model.datasetDescription })}</Text>
      ) : null}
      {model.notes ? <Text style={styles.modelMeta}>{model.notes}</Text> : null}

      {model.latestEvaluations.length === 0 ? (
        // Said once, in words. The dashes it replaces read as "scored zero", which is not
        // what happened: nothing has asked the question yet. Which is followed by why not —
        // the four reasons are four different things to do next, and only one of them is "wait".
        <>
          <Text style={styles.notEvaluated} testID={`ml-model-not-evaluated-${model.name}-${model.version}`}>
            {t('mlModels.notEvaluated')}
          </Text>
          <Text style={styles.notEvaluatedWhy} testID={`ml-model-why-${model.name}-${model.version}`}>
            {reasonText(evaluationStatusOf(model, jobs), t)}
          </Text>
        </>
      ) : (
        <View style={styles.ratings}>
          {KINDS.map((kind) => (
            <Rating key={kind} kind={kind} evaluation={byKind.get(kind) ?? null} model={model} />
          ))}
        </View>
      )}

      {open ? <ModelDetail model={model} params={params} miou={miou} /> : null}
    </View>
  );
}

/**
 * Everything the registry holds about one model, opened from its card: the identity, the run,
 * the courses on both sides of the split, and the raw numbers behind each star.
 *
 * A list of label/value rows rather than a shaped layout, because the value that matters
 * changes with the question — a course id to paste into a query one day, an F1's second
 * decimal the next — and every row is worth the same amount.
 */
function ModelDetail({
  model,
  params,
  miou,
}: {
  model: MlModel;
  params: ReturnType<typeof describeHyperparams>;
  miou: number | null;
}) {
  const { t } = useI18n();
  const router = useRouter();
  const rows: [string, string][] = [];
  const add = (label: string, value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return;
    rows.push([label, String(value)]);
  };

  add(t('mlModels.detailId'), model.id);
  add(t('mlModels.detailName'), `${model.name} · ${model.version}`);
  add(t('mlModels.detailArchitecture'), model.architecture);
  add(t('mlModels.detailTrainedAt'), model.trainedAt ? formatWhenExact(model.trainedAt) : null);
  add(t('mlModels.detailEncoder'), params.encoder);
  add(t('mlModels.detailChannels'), params.inChannels);
  add(t('mlModels.detailEpochs'), model.epochs > 0 ? model.epochs : null);
  add(t('mlModels.detailBatchSize'), params.batchSize);
  add(t('mlModels.detailLearningRate'), params.learningRate);
  add(t('mlModels.detailMiou'), miou !== null ? miou.toFixed(4) : null);
  // The stored column, as stored: it is what a query against the registry returns, and the
  // card's mIoU is 1 − it.
  add(t('mlModels.detailBestValLoss'), model.bestValLoss !== null ? model.bestValLoss.toFixed(4) : null);
  add(t('mlModels.detailProviders'), params.providers.join(', '));
  add(t('mlModels.detailTrainSet'), params.trainSet);
  add(t('mlModels.detailTrainCrops'), params.trainCrops);
  add(t('mlModels.detailDatasetId'), model.datasetId);
  add(t('mlModels.detailDataset'), model.datasetDescription);
  add(
    t('mlModels.detailCheckpoint'),
    model.checkpointS3Key ?? t('mlModels.checkpointLocal')
  );
  add(t('mlModels.detailNotes'), model.notes);

  return (
    <View style={styles.detail} testID={`ml-model-detail-${model.name}-${model.version}`}>
      {rows.map(([label, value]) => (
        <View key={label} style={styles.detailRow}>
          <Text style={styles.detailLabel}>{label}</Text>
          <Text style={styles.detailValue} selectable>
            {value}
          </Text>
        </View>
      ))}

      {/* By id, listed rather than counted: which six courses a model saw is the difference
          between two runs that both say "6 courses", and the holdout ids are what an
          evaluate.py run is given. */}
      <CourseIds label={t('mlModels.detailTrainCourses')} ids={params.trainCourseIds} />
      {/* The holdout is where the evaluation's polygons live: each course opens on the admin
          hole map with OSM and the evaluation layers on, which is the picture behind the stars. */}
      <CourseIds
        label={t('mlModels.detailHoldoutCourses')}
        ids={params.holdoutCourses}
        onOpen={(courseId) => router.push({ pathname: '/golf-course-admin', params: { holeMap: courseId } })}
      />

      {model.latestEvaluations.length > 0 ? (
        <View style={styles.detailBlock}>
          <Text style={styles.detailLabel}>{t('mlModels.detailEvaluations')}</Text>
          {model.latestEvaluations.map((evaluation) => (
            <Text key={evaluation.id} style={styles.detailValue} selectable>
              {t('mlModels.detailEvaluationLine', {
                kind: t(`courseAnalysis.kind.${evaluation.kind}`),
                f1: evaluation.f1.toFixed(3),
                p: evaluation.precision.toFixed(3),
                r: evaluation.recall.toFixed(3),
                iou: evaluation.iou !== null ? evaluation.iou.toFixed(3) : '—',
                matched: evaluation.matched,
                missed: evaluation.missed,
                falsePositive: evaluation.falsePositive,
                courses: evaluation.courseCount,
                provider: evaluation.provider,
                when: evaluation.evaluatedAt ? formatWhenExact(evaluation.evaluatedAt) : '',
              })}
            </Text>
          ))}
        </View>
      ) : null}
    </View>
  );
}

/** A list of course ids under one label, or the label saying there are none. */
function CourseIds({
  label,
  ids,
  onOpen,
}: {
  label: string;
  ids: string[];
  /** Given, each id also opens somewhere — the holdout courses open on the hole map. */
  onOpen?: (courseId: string) => void;
}) {
  const { t } = useI18n();
  return (
    <View style={styles.detailBlock}>
      <Text style={styles.detailLabel}>{label}</Text>
      {ids.length === 0 ? (
        <Text style={styles.detailValue}>{t('mlModels.detailNone')}</Text>
      ) : (
        ids.map((id) => (
          <View key={id} style={styles.detailRow}>
            <Text style={styles.detailValue} selectable>
              {id}
            </Text>
            {onOpen ? (
              <Pressable accessibilityRole="link" testID={`ml-model-open-course-${id}`} onPress={() => onOpen(id)}>
                <Text style={styles.disclosure}>{t('mlModels.detailViewOnCourse')}</Text>
              </Pressable>
            ) : null}
          </View>
        ))
      )}
    </View>
  );
}

function Rating({ kind, evaluation, model }: { kind: string; evaluation: MlEvaluation | null; model: MlModel }) {
  const { t } = useI18n();
  const stars = evaluation ? starsFor(evaluation.f1) : null;
  return (
    <View style={styles.rating} testID={`ml-model-rating-${model.name}-${model.version}-${kind}`}>
      <Text style={styles.ratingKind}>{t(`courseAnalysis.kind.${kind}`)}</Text>
      <Text style={styles.stars} accessibilityLabel={stars !== null ? t('mlModels.starsLabel', { stars }) : undefined}>
        {stars !== null ? (
          <>
            <Text style={styles.starOn}>{'★'.repeat(stars)}</Text>
            <Text style={styles.starOff}>{'☆'.repeat(5 - stars)}</Text>
          </>
        ) : (
          <Text style={styles.starOff}>{t('mlModels.noScore')}</Text>
        )}
      </Text>
      {evaluation ? (
        <Text style={styles.ratingDetail}>
          {[
            t('mlModels.scoreDetail', {
              f1: evaluation.f1.toFixed(2),
              p: evaluation.precision.toFixed(2),
              r: evaluation.recall.toFixed(2),
            }),
            evaluation.courseCount > 0 ? t('mlModels.scoredOn', { count: evaluation.courseCount }) : null,
          ]
            .filter(Boolean)
            .join('  ·  ')}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Five stars from an F1. Whole stars, rounded: 0.66 is three, 0.9 is four and a half rounded
 * up to five. A finer scale would imply the second decimal of an F1 measured on two courses
 * means something.
 */
export function starsFor(f1: number): number {
  if (!Number.isFinite(f1)) return 0;
  return Math.max(0, Math.min(5, Math.round(f1 * 5)));
}

/**
 * Why a model has no stars. Four of these are four different things to do next — file a run,
 * wait for a worker, wait for imagery, read an error — and the card used to say none of them.
 *
 * The evidence is the model's own hyperparams and the page's job list: an `evaluate` run names
 * the model it scores in its arguments (`--model-name`, `--model-version`), which is how
 * geodata files it after a training run and how it is found again here.
 */
export type EvaluationStatus =
  | { state: 'evaluated' }
  /** train.py recorded no holdout course, so nothing was filed and nothing honest can be. */
  | { state: 'noHoldout' }
  | { state: 'waitingForImagery' }
  | { state: 'queued' }
  | { state: 'failed'; error: string | null }
  | { state: 'cancelled' }
  /** The run came back clean and registered nothing — an evaluation that found no truth to score against. */
  | { state: 'ranWithoutScore' }
  /** Holdout courses, no evaluate run: the follow-up never happened, and can be filed by hand. */
  | { state: 'notFiled' }
  /**
   * There is no score, and no job list to say why. Distinct from `notFiled` on purpose: a page
   * that has not loaded the runs cannot claim none was ever filed, and saying so sends a person
   * to queue a second evaluation of a model that already has one.
   */
  | { state: 'unknown' };

export function evaluationStatusOf(model: MlModel, jobs: readonly MlModelJob[] | null): EvaluationStatus {
  if (model.latestEvaluations.length > 0) return { state: 'evaluated' };
  if (describeHyperparams(model.hyperparamsJson).holdoutCourses.length === 0) return { state: 'noHoldout' };
  if (jobs === null) return { state: 'unknown' };

  const latest = jobs
    .filter((job) => job.kind === 'evaluate' && namesModel(job.args, model))
    .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))[0];
  if (!latest) return { state: 'notFiled' };

  switch (latest.status) {
    case 'awaiting-imagery':
      return { state: 'waitingForImagery' };
    case 'queued':
      return { state: 'queued' };
    case 'failed':
      return { state: 'failed', error: latest.error };
    case 'cancelled':
      return { state: 'cancelled' };
    default:
      return { state: 'ranWithoutScore' };
  }
}

/** Whether an evaluate run's arguments name this model — the pairing geodata files it under. */
const namesModel = (args: readonly string[], model: MlModel): boolean =>
  args.some((arg, i) => arg === '--model-name' && args[i + 1] === model.name) &&
  args.some((arg, i) => arg === '--model-version' && args[i + 1] === model.version);

const reasonText = (status: EvaluationStatus, t: (key: string, params?: Record<string, string | number>) => string): string => {
  switch (status.state) {
    case 'noHoldout':
      return t('mlModels.whyNoHoldout');
    case 'waitingForImagery':
      return t('mlModels.whyWaitingForImagery');
    case 'queued':
      return t('mlModels.whyQueued');
    case 'failed':
      return t('mlModels.whyFailed', { error: status.error ?? '' });
    case 'cancelled':
      return t('mlModels.whyCancelled');
    case 'ranWithoutScore':
      return t('mlModels.whyRanWithoutScore');
    case 'notFiled':
      return t('mlModels.whyNotFiled');
    default:
      return t('mlModels.whyUnknown');
  }
};

/** The registry stores 1 − mIoU under "best validation loss"; people quote the mIoU. */
export function trainingMiou(bestValLoss: number | null): number | null {
  if (bestValLoss === null || !Number.isFinite(bestValLoss)) return null;
  return Math.max(0, Math.min(1, 1 - bestValLoss));
}

/**
 * What train.py recorded about a run, out of the hyperparams it stored verbatim. Every field
 * is optional: the keys are train.py's, and a row from another version of it still renders.
 */
export function describeHyperparams(hyperparamsJson: string | null): {
  encoder: string | null;
  inChannels: number | null;
  batchSize: number | null;
  learningRate: number | null;
  trainCrops: number | null;
  trainCourses: number | null;
  /** The courses the run trained on, by id — the count is the card's, the ids are the detail's. */
  trainCourseIds: string[];
  providers: string[];
  /** The courses train.py held out, by id — the set an honest evaluation of this model runs on. */
  holdoutCourses: string[];
  /** The named course set the run trained on — its size, in the word a person chose for it. */
  trainSet: string | null;
} {
  const empty = {
    encoder: null,
    inChannels: null,
    batchSize: null,
    learningRate: null,
    trainCrops: null,
    trainCourses: null,
    trainCourseIds: [],
    providers: [],
    holdoutCourses: [],
    trainSet: null,
  };
  if (!hyperparamsJson) return empty;
  let parsed: unknown;
  try {
    parsed = JSON.parse(hyperparamsJson);
  } catch {
    return empty;
  }
  if (!parsed || typeof parsed !== 'object') return empty;
  const row = parsed as Record<string, unknown>;
  const numberOrNull = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
  const courses = row.trainCourses;
  const providers = row.providers;
  const holdout = row.holdoutCourses;
  return {
    encoder: typeof row.encoder === 'string' ? row.encoder : null,
    inChannels: numberOrNull(row.inChannels),
    batchSize: numberOrNull(row.batchSize),
    learningRate: numberOrNull(row.learningRate),
    trainCrops: numberOrNull(row.trainCrops),
    // Stored as the list of course ids; the count is what the card has room for.
    trainCourses: Array.isArray(courses) ? courses.length : numberOrNull(courses),
    trainCourseIds: Array.isArray(courses) ? courses.filter((c): c is string => typeof c === 'string') : [],
    providers: Array.isArray(providers) ? providers.filter((p): p is string => typeof p === 'string') : [],
    holdoutCourses: Array.isArray(holdout) ? holdout.filter((c): c is string => typeof c === 'string') : [],
    trainSet: typeof row.trainSet === 'string' && row.trainSet.length > 0 ? row.trainSet : null,
  };
}

const formatWhen = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString();
};

/** The date and the time: two runs of the same day are minutes apart, and the card shows only the day. */
const formatWhenExact = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const styles = StyleSheet.create({
  openButton: {
    alignSelf: 'stretch',
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.background.panel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  openButtonText: {
    color: appColors.accent.primary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  card: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 14,
    gap: 10,
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
  close: { color: appColors.accent.primary, fontSize: 12, fontWeight: '700' },
  notice: { color: appColors.text.secondary, fontSize: 13 },
  legend: { color: appColors.text.secondary, fontSize: 11, lineHeight: 15 },
  model: { borderTopWidth: 1, borderTopColor: appColors.border.strong, paddingTop: 10, gap: 3 },
  modelHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  modelHeading: { flexShrink: 1, gap: 3 },
  disclosure: { color: appColors.accent.primary, fontSize: 11, fontWeight: '700' },
  modelName: { color: appColors.text.primary, fontSize: 14, fontWeight: '700' },
  modelMeta: { color: appColors.text.secondary, fontSize: 11 },
  facts: { color: appColors.text.primary, fontSize: 12, lineHeight: 17 },
  notEvaluated: { color: appColors.accent.goldMuted, fontSize: 12, marginTop: 4 },
  notEvaluatedWhy: { color: appColors.text.secondary, fontSize: 12, lineHeight: 17 },
  detail: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: appColors.border.strong,
    gap: 4,
  },
  detailRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  detailBlock: { marginTop: 4, gap: 1 },
  detailLabel: { color: appColors.text.secondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 },
  detailValue: { color: appColors.text.primary, fontSize: 12, lineHeight: 17 },
  ratings: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 6 },
  rating: { minWidth: 120, gap: 1 },
  ratingKind: { color: appColors.text.secondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 },
  stars: { fontSize: 16, letterSpacing: 1 },
  starOn: { color: appColors.accent.goldMuted },
  starOff: { color: appColors.text.muted },
  ratingDetail: { color: appColors.text.secondary, fontSize: 11 },
});
